import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '../components/ui/sheet';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Plus, Search, Phone, Mail, Calendar, MapPin, Clock,
  AlertTriangle, CheckCircle, XCircle, ArrowRight,
  MessageSquare, IndianRupee, Send, Filter, LayoutGrid, 
  List, CalendarDays, Thermometer, ChevronRight, GripVertical,
  MessageCircle, RefreshCw, Camera, Copy, PhoneCall,
  PhoneOff, Upload, Image, X
} from 'lucide-react';
import { toast } from 'sonner';

const PIPELINE_STAGES = [
  { value: 'new_enquiry', label: 'New Enquiry', color: 'bg-blue-500' },
  { value: 'follow_up', label: 'Follow-up', color: 'bg-purple-500' },
  { value: 'quote_sent', label: 'Quote Sent', color: 'bg-amber-500' },
  { value: 'negotiation', label: 'Negotiation', color: 'bg-orange-500' },
  { value: 'booked', label: 'Booked', color: 'bg-emerald-500' },
  { value: 'lost', label: 'Lost', color: 'bg-rose-500' },
];

const LEAD_SOURCES_WS = ['Instagram', 'Google', 'Direct', 'Referral', 'Other'];
const LEAD_SOURCES_TB = ['Instagram', 'Google', 'Direct', 'Referral', 'Other'];
const LOST_REASONS = ['Went to competitor', 'Budget too low', 'Date not available', 'No response', 'Other'];

const openWhatsApp = (phone, name, message) => {
  const cleaned = phone.replace(/[^0-9]/g, '');
  const num = cleaned.startsWith('91') ? cleaned : `91${cleaned}`;
  const text = message || `Hi ${name}, this is from Wittyshadows.`;
  window.open(`https://wa.me/${num}?text=${encodeURIComponent(text)}`, '_blank');
};

const copyToClipboard = (text) => {
  navigator.clipboard.writeText(text).then(() => toast.success('Copied!')).catch(() => toast.error('Copy failed'));
};

const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
const formatDateTime = (d) => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
const formatCurrency = (v) => v ? `₹${Number(v).toLocaleString('en-IN')}` : '-';

const getStageBadge = (stage) => {
  const m = { new_enquiry: 'bg-blue-500/10 text-blue-400 border-blue-500/20', follow_up: 'bg-purple-500/10 text-purple-400 border-purple-500/20', quote_sent: 'bg-amber-500/10 text-amber-400 border-amber-500/20', negotiation: 'bg-orange-500/10 text-orange-400 border-orange-500/20', booked: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', lost: 'bg-rose-500/10 text-rose-400 border-rose-500/20' };
  return m[stage] || m.new_enquiry;
};

const getScoreColor = (label) => {
  if (label === 'hot') return 'bg-rose-500/10 text-rose-400 border-rose-500/20';
  if (label === 'warm') return 'bg-amber-500/10 text-amber-400 border-amber-500/20';
  return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
};
const getScoreDot = (label) => label === 'hot' ? 'bg-rose-400' : label === 'warm' ? 'bg-amber-400' : 'bg-blue-400';

const LeadsPage = () => {
  const { user } = useAuth();
  const isManager = (user?.roles || [user?.role]).some(r => ['admin', 'manager'].includes(r));
  const [leads, setLeads] = useState([]);
  const [salesPeople, setSalesPeople] = useState([]);
  const [todayFollowUps, setTodayFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('kanban');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSource, setFilterSource] = useState('all');
  const [filterBrand, setFilterBrand] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  // Dialogs
  const [showBrandPicker, setShowBrandPicker] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDetailSheet, setShowDetailSheet] = useState(false);
  const [showLostDialog, setShowLostDialog] = useState(false);
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [showCallLog, setShowCallLog] = useState(false);
  const [showPhoneActions, setShowPhoneActions] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);

  // Create form
  const [selectedBrand, setSelectedBrand] = useState('wittyshadows');
  const [newLead, setNewLead] = useState({
    client_name: '', phone: '', event_date: '', lead_source: 'Instagram',
    lead_source_other: '', assigned_to: '', notes: ''
  });
  const [screenshotFile, setScreenshotFile] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Detail form states
  const [noteText, setNoteText] = useState('');
  const [lostForm, setLostForm] = useState({ lost_reason: '', lost_notes: '' });
  const [convertForm, setConvertForm] = useState({ advance_amount: '', project_title: '' });
  const [draggedLead, setDraggedLead] = useState(null);

  // Call log form
  const [callForm, setCallForm] = useState({
    connected: false, notes: '', callback_time: '', lead_quality: '',
    quote_amount: '', follow_up_date: '', wa_message_sent: false
  });

  const phoneActionsRef = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let url = '/leads';
      const params = [];
      if (filterBrand !== 'all') params.push(`brand=${filterBrand}`);
      if (params.length) url += '?' + params.join('&');
      const leadsRes = await api.get(url);
      setLeads(leadsRes.data || []);
      // Users list is only needed for managers (to assign leads). Sales people can't access /users (403).
      if (isManager) {
        try {
          const spRes = await api.get('/users');
          setSalesPeople((spRes.data || []).filter(u => {
            const roles = u.roles || [u.role];
            return roles.some(r => ['admin', 'manager', 'sales_person'].includes(r));
          }));
        } catch (e) {
          setSalesPeople([]);
        }
      }
    } catch (err) {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [filterBrand, isManager]);

  const fetchTodayFollowUps = useCallback(async () => {
    try {
      const res = await api.get('/leads/today-followups');
      setTodayFollowUps(res.data);
    } catch (err) {
      console.error('Failed to fetch follow-ups');
    }
  }, []);

  useEffect(() => { fetchData(); fetchTodayFollowUps(); }, [fetchData, fetchTodayFollowUps]);

  // Close phone actions on outside click
  useEffect(() => {
    const handler = (e) => {
      if (phoneActionsRef.current && !phoneActionsRef.current.contains(e.target)) setShowPhoneActions(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ==================== HANDLERS ====================

  const handleNewEnquiry = () => {
    if (isManager) {
      setShowBrandPicker(true);
    } else {
      setSelectedBrand(user?.brand || 'wittyshadows');
      setShowCreateDialog(true);
    }
  };

  const handleBrandSelect = (brand) => {
    setSelectedBrand(brand);
    setShowBrandPicker(false);
    setNewLead({ client_name: '', phone: '', event_date: '', lead_source: 'Instagram', lead_source_other: '', assigned_to: '', notes: '' });
    setScreenshotFile(null);
    setScreenshotPreview(null);
    setShowCreateDialog(true);
  };

  const handleScreenshotSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setScreenshotFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setScreenshotPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleCreateLead = async () => {
    if (!newLead.client_name || !newLead.phone) {
      toast.error('Name and phone are required');
      return;
    }
    setUploading(true);
    try {
      // Create lead
      const payload = {
        client_name: newLead.client_name,
        phone: newLead.phone,
        lead_source: newLead.lead_source,
        lead_source_other: newLead.lead_source === 'Other' ? newLead.lead_source_other : '',
        brand: selectedBrand,
        event_date: newLead.event_date ? new Date(newLead.event_date).toISOString() : undefined,
        assigned_to: newLead.assigned_to || undefined,
        notes: newLead.notes || undefined
      };
      const res = await api.post('/leads', payload);
      const leadId = res.data.id;

      // Upload screenshot if present
      if (screenshotFile) {
        const formData = new FormData();
        formData.append('file', screenshotFile);
        await api.post(`/leads/${leadId}/screenshots`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      toast.success('Lead created successfully');
      setShowCreateDialog(false);
      resetCreateForm();
      fetchData();
      fetchTodayFollowUps();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create lead');
    } finally {
      setUploading(false);
    }
  };

  const resetCreateForm = () => {
    setNewLead({ client_name: '', phone: '', event_date: '', lead_source: 'Instagram', lead_source_other: '', assigned_to: '', notes: '' });
    setScreenshotFile(null);
    setScreenshotPreview(null);
  };

  const handleLeadClick = async (lead) => {
    try {
      const res = await api.get(`/leads/${lead.id}`);
      setSelectedLead({ ...lead, ...res.data });
      setShowDetailSheet(true);
    } catch {
      setSelectedLead(lead);
      setShowDetailSheet(true);
    }
  };

  const handleStageChange = async (leadId, stage) => {
    try {
      await api.patch(`/leads/${leadId}/stage`, { stage });
      toast.success(`Lead moved to ${stage.replace('_', ' ')}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to move lead');
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim() || !selectedLead) return;
    try {
      await api.post(`/leads/${selectedLead.id}/notes`, { text: noteText });
      setNoteText('');
      const res = await api.get(`/leads/${selectedLead.id}`);
      setSelectedLead({ ...selectedLead, ...res.data });
      fetchData();
    } catch (err) { toast.error('Failed to add note'); }
  };

  const handleUpdateField = async (leadId, field, value) => {
    try {
      await api.patch(`/leads/${leadId}`, { [field]: value });
      fetchData();
    } catch (err) { toast.error('Failed to update'); }
  };

  const handleSetQuality = async (leadId, value) => {
    try {
      await api.patch(`/leads/${leadId}`, { lead_quality: value });
      // Optimistic local update so the row reflects immediately without flicker
      setLeads(prev => prev.map(l => l.id === leadId ? { ...l, lead_quality: value } : l));
      toast.success(`Marked as ${value}`);
    } catch (err) { toast.error('Failed to tag quality'); }
  };

  const handleMarkLost = async () => {
    const reason = (lostForm.lost_reason || '').trim();
    if (!reason) { toast.error('Please enter a reason'); return; }
    try {
      await api.post(`/leads/${selectedLead.id}/lost`, { ...lostForm, lost_reason: reason });
      setShowLostDialog(false);
      setShowDetailSheet(false);
      fetchData();
      toast.success('Lead marked as lost');
    } catch (err) { toast.error('Failed'); }
  };

  const handleConvert = async () => {
    if (!convertForm.advance_amount) { toast.error('Enter advance amount'); return; }
    try {
      await api.post(`/leads/${selectedLead.id}/convert`, {
        advance_amount: parseFloat(convertForm.advance_amount),
        project_title: convertForm.project_title
      });
      setShowConvertDialog(false);
      setShowDetailSheet(false);
      fetchData();
      toast.success('Lead converted to booking!');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to convert'); }
  };

  const handleLogCall = async () => {
    if (!selectedLead) return;
    try {
      const payload = {
        connected: callForm.connected,
        notes: callForm.notes,
        wa_message_sent: callForm.wa_message_sent
      };
      if (callForm.connected) {
        if (callForm.lead_quality) payload.lead_quality = callForm.lead_quality;
        if (callForm.quote_amount) payload.quote_amount = callForm.quote_amount;
        if (callForm.follow_up_date) payload.follow_up_date = new Date(callForm.follow_up_date).toISOString();
      } else {
        if (callForm.callback_time) payload.callback_time = new Date(callForm.callback_time).toISOString();
      }
      await api.post(`/leads/${selectedLead.id}/call-log`, payload);
      toast.success('Call logged');
      setShowCallLog(false);
      setCallForm({ connected: false, notes: '', callback_time: '', lead_quality: '', quote_amount: '', follow_up_date: '', wa_message_sent: false });
      const res = await api.get(`/leads/${selectedLead.id}`);
      setSelectedLead({ ...selectedLead, ...res.data });
      fetchData();
      fetchTodayFollowUps();
    } catch (err) { toast.error('Failed to log call'); }
  };

  const handleScreenshotUploadForLead = async (leadId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/leads/${leadId}/screenshots`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Screenshot uploaded');
      const res = await api.get(`/leads/${leadId}`);
      setSelectedLead(prev => ({ ...prev, ...res.data }));
    } catch (err) { toast.error('Upload failed'); }
  };

  // Drag and drop
  const handleDragStart = (e, lead) => { setDraggedLead(lead); e.dataTransfer.effectAllowed = 'move'; };
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = (e, stage) => { e.preventDefault(); if (draggedLead && draggedLead.pipeline_stage !== stage) handleStageChange(draggedLead.id, stage); setDraggedLead(null); };

  // Filtering (defensive: skip malformed leads)
  const filteredLeads = (Array.isArray(leads) ? leads : []).filter(l => {
    if (!l || !l.id) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const name = (l.client_name || '').toLowerCase();
      const phone = l.phone || '';
      if (!name.includes(q) && !phone.includes(q)) return false;
    }
    if (filterSource !== 'all' && l.lead_source !== filterSource) return false;
    return true;
  }).sort((a, b) => {
    if (sortBy === 'oldest') return new Date(a.created_at) - new Date(b.created_at);
    if (sortBy === 'score') return (b.lead_score || 0) - (a.lead_score || 0);
    return new Date(b.created_at) - new Date(a.created_at);
  });

  const getStageLeads = (stage) => filteredLeads.filter(l => l.pipeline_stage === stage);
  const getStageValue = (stage) => getStageLeads(stage).reduce((sum, l) => sum + (l.quote_amount || 0), 0);
  const allSources = [...new Set(leads.map(l => l.lead_source))];

  // ==================== RENDER ====================

  return (
    <div className="space-y-3" data-testid="leads-page">
      {/* Today's Follow-ups Banner */}
      {todayFollowUps.length > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4" data-testid="today-followups-banner">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-4 w-4 text-amber-400" />
            <span className="text-amber-400 font-semibold text-sm">{todayFollowUps.length} Follow-up{todayFollowUps.length > 1 ? 's' : ''} Today</span>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-1">
            {todayFollowUps.map(fu => (
              <div key={fu.id}
                   onClick={() => handleLeadClick(fu)}
                   className={`flex-shrink-0 px-3 py-2 rounded-lg border cursor-pointer transition-colors hover:border-amber-500/50 ${fu.is_overdue ? 'bg-rose-500/10 border-rose-500/20' : 'bg-[#121212] border-white/10'}`}
                   data-testid={`followup-${fu.id}`}>
                <div className="flex items-center gap-2">
                  <span className="text-white text-sm font-medium">{fu.client_name}</span>
                  {fu.is_overdue && <Badge variant="outline" className="text-[10px] bg-rose-500/10 text-rose-400 border-rose-500/20 px-1.5">OVERDUE</Badge>}
                  <Badge variant="outline" className={`text-[10px] ${fu.brand === 'trio_bliss' ? 'bg-purple-500/10 text-purple-400 border-purple-500/20' : 'bg-blue-500/10 text-blue-400 border-blue-500/20'}`}>
                    {fu.brand === 'trio_bliss' ? 'TB' : 'WS'}
                  </Badge>
                </div>
                <div className="flex items-center gap-3 mt-1">
                  <button onClick={(e) => { e.stopPropagation(); copyToClipboard(fu.phone); }} className="text-xs text-zinc-400 hover:text-white flex items-center gap-1">
                    <Phone className="h-3 w-3" />{fu.phone}
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); openWhatsApp(fu.phone, fu.client_name); }} className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1">
                    <MessageCircle className="h-3 w-3" />WA
                  </button>
                  {fu.call_status === 'not_connected' && <span className="text-xs text-rose-400"><PhoneOff className="h-3 w-3 inline" /> Not connected</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-white tracking-tight">Leads & Sales</h1>
          <p className="text-zinc-500 text-xs">{isManager ? 'All team leads' : 'Your leads'} &middot; {leads.length} total</p>
        </div>
        <div className="flex items-center gap-2">
          <Button className="bg-[#F5A623] hover:bg-[#F5A623]/90 text-black h-8 text-sm" onClick={handleNewEnquiry} data-testid="add-lead-btn">
            <Plus className="h-3.5 w-3.5 mr-1.5" /> New Enquiry
          </Button>
          <Button variant="outline" size="sm" onClick={() => { fetchData(); fetchTodayFollowUps(); }}
            className="border-[#F5A623]/30 text-[#F5A623] hover:bg-[#F5A623]/10 h-8" data-testid="refresh-leads">
            <RefreshCw className="h-3 w-3" />
          </Button>
          {isManager && (
            <Button variant="outline" size="sm"
              onClick={async () => {
                try {
                  const res = await api.get('/leads/export.csv?format=ads', { responseType: 'blob' });
                  const blob = new Blob([res.data], { type: 'text/csv' });
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `leads-ads-${new Date().toISOString().slice(0,10)}.csv`;
                  a.click();
                  window.URL.revokeObjectURL(url);
                  toast.success('Exported. Open in Google Ads → Upload Conversions');
                } catch (e) {
                  toast.error('Export failed');
                }
              }}
              className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 h-8 text-xs"
              data-testid="export-leads-csv">
              Export for Ads
            </Button>
          )}
        </div>
      </div>

      {/* Search, Filter, Brand Filter, View Toggle */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input placeholder="Search..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="bg-[#121212] border-white/10 text-white pl-9 h-8 text-sm" data-testid="lead-search" />
        </div>
        {isManager && (
          <Select value={filterBrand} onValueChange={v => setFilterBrand(v)}>
            <SelectTrigger className="w-40 bg-[#121212] border-white/10 text-white" data-testid="brand-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/10">
              <SelectItem value="all" className="text-white hover:bg-white/10">All Brands</SelectItem>
              <SelectItem value="wittyshadows" className="text-white hover:bg-white/10">Wittyshadows</SelectItem>
              <SelectItem value="trio_bliss" className="text-white hover:bg-white/10">Trio Bliss</SelectItem>
            </SelectContent>
          </Select>
        )}
        <Select value={filterSource} onValueChange={setFilterSource}>
          <SelectTrigger className="w-36 bg-[#121212] border-white/10 text-white" data-testid="source-filter">
            <Filter className="h-4 w-4 mr-2 text-zinc-500" /><SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#121212] border-white/10">
            <SelectItem value="all" className="text-white hover:bg-white/10">All Sources</SelectItem>
            {allSources.map(s => <SelectItem key={s} value={s} className="text-white hover:bg-white/10">{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-36 bg-[#121212] border-white/10 text-white" data-testid="sort-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#121212] border-white/10">
            <SelectItem value="newest" className="text-white hover:bg-white/10">Newest First</SelectItem>
            <SelectItem value="oldest" className="text-white hover:bg-white/10">Oldest First</SelectItem>
            <SelectItem value="score" className="text-white hover:bg-white/10">Highest Score</SelectItem>
          </SelectContent>
        </Select>
        <Tabs value={viewMode} onValueChange={setViewMode}>
          <TabsList className="bg-[#121212] border border-white/10 h-8">
            <TabsTrigger value="kanban" className="h-6 px-2 data-[state=active]:bg-white/10"><LayoutGrid className="h-3.5 w-3.5" /></TabsTrigger>
            <TabsTrigger value="list" className="h-6 px-2 data-[state=active]:bg-white/10"><List className="h-3.5 w-3.5" /></TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* ===== KANBAN VIEW ===== */}
      {viewMode === 'kanban' && (
        <div className="flex gap-3 overflow-x-auto pb-2" data-testid="kanban-board">
          {PIPELINE_STAGES.map(stage => (
            <div key={stage.value} className="min-w-[220px] max-w-[240px] flex-shrink-0"
              onDragOver={handleDragOver} onDrop={(e) => handleDrop(e, stage.value)}>
              <div className="flex items-center justify-between mb-2 px-1">
                <div className="flex items-center gap-1.5">
                  <div className={`h-2 w-2 rounded-full ${stage.color}`} />
                  <span className="text-xs font-medium text-white">{stage.label}</span>
                  <Badge variant="outline" className="text-[10px] bg-white/5 text-zinc-400 border-white/10 px-1">{getStageLeads(stage.value).length}</Badge>
                </div>
                {getStageValue(stage.value) > 0 && <span className="text-xs text-zinc-500">{formatCurrency(getStageValue(stage.value))}</span>}
              </div>
              <div className="space-y-1.5 min-h-[60px] bg-white/[0.02] rounded-lg p-1.5 border border-white/5">
                {getStageLeads(stage.value).map(lead => (
                  <LeadCard key={lead.id} lead={lead} onDragStart={handleDragStart} onClick={handleLeadClick}
                    onPhoneAction={(id) => setShowPhoneActions(showPhoneActions === id ? null : id)}
                    showPhoneActions={showPhoneActions === lead.id} phoneActionsRef={phoneActionsRef}
                    onSetQuality={handleSetQuality} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ===== LIST VIEW ===== */}
      {viewMode === 'list' && (
        <div className="space-y-2">
          {filteredLeads.length === 0 ? (
            <div className="text-center py-12 text-zinc-500"><Search className="h-10 w-10 mx-auto mb-3 opacity-30" /><p>No leads found</p></div>
          ) : filteredLeads.map(lead => (
            <div key={lead.id} onClick={() => handleLeadClick(lead)}
              className="flex items-center gap-4 p-4 bg-[#121212] border border-white/10 rounded-lg cursor-pointer hover:border-[#F5A623]/30 transition-colors"
              data-testid={`lead-row-${lead.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-white font-medium text-sm truncate">{lead.client_name}</p>
                  <Badge variant="outline" className={`text-[10px] ${lead.brand === 'trio_bliss' ? 'bg-purple-500/10 text-purple-400 border-purple-500/20' : 'bg-blue-500/10 text-blue-400 border-blue-500/20'}`}>
                    {lead.brand === 'trio_bliss' ? 'TB' : 'WS'}
                  </Badge>
                  {lead.lead_score != null && (
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold border ${getScoreColor(lead.score_label)}`}>
                      <span className={`inline-block h-1.5 w-1.5 rounded-full mr-0.5 ${getScoreDot(lead.score_label)}`} />{lead.lead_score}
                    </span>
                  )}
                  {lead.call_status === 'not_connected' && <PhoneOff className="h-3 w-3 text-rose-400" />}
                  {lead.call_status === 'connected' && <PhoneCall className="h-3 w-3 text-emerald-400" />}
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-zinc-500">
                  <button onClick={(e) => { e.stopPropagation(); copyToClipboard(lead.phone); }} className="hover:text-white flex items-center gap-1"><Phone className="h-3 w-3" />{lead.phone}</button>
                  <span>{lead.lead_source}</span>
                  {lead.event_type && <span>{lead.event_type}</span>}
                  {lead.assigned_to_name && <span>Assigned: {lead.assigned_to_name}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <QualityButtons leadId={lead.id} current={lead.lead_quality} onSet={handleSetQuality} />
                {lead.quote_amount && <span className="text-[#F5A623] text-sm font-medium">{formatCurrency(lead.quote_amount)}</span>}
                <Badge variant="outline" className={`text-xs ${getStageBadge(lead.pipeline_stage)}`}>{(lead.pipeline_stage || 'new_enquiry').replace(/_/g, ' ')}</Badge>
                {lead.follow_up_status === 'overdue' && <Clock className="h-3.5 w-3.5 text-rose-400" />}
                {lead.follow_up_status === 'due_today' && <Clock className="h-3.5 w-3.5 text-amber-400" />}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ===== BRAND PICKER DIALOG ===== */}
      <Dialog open={showBrandPicker} onOpenChange={setShowBrandPicker}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md">
          <DialogHeader><DialogTitle>Select Brand</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <button onClick={() => handleBrandSelect('wittyshadows')}
              className="p-6 rounded-lg border-2 border-white/10 hover:border-[#F5A623]/50 bg-[#0A0A0A] transition-all hover:scale-[1.02] text-center group"
              data-testid="brand-wittyshadows">
              <Camera className="h-8 w-8 mx-auto mb-3 text-[#F5A623] group-hover:scale-110 transition-transform" />
              <p className="text-white font-semibold">Wittyshadows</p>
              <p className="text-xs text-zinc-500 mt-1">Photography Studio</p>
            </button>
            <button onClick={() => handleBrandSelect('trio_bliss')}
              className="p-6 rounded-lg border-2 border-white/10 hover:border-purple-500/50 bg-[#0A0A0A] transition-all hover:scale-[1.02] text-center group"
              data-testid="brand-triobliss">
              <Camera className="h-8 w-8 mx-auto mb-3 text-purple-400 group-hover:scale-110 transition-transform" />
              <p className="text-white font-semibold">Trio Bliss</p>
              <p className="text-xs text-zinc-500 mt-1">Event Studio</p>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== CREATE LEAD DIALOG ===== */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              New Enquiry
              <Badge variant="outline" className={`text-xs ${selectedBrand === 'trio_bliss' ? 'bg-purple-500/10 text-purple-400 border-purple-500/20' : 'bg-[#F5A623]/10 text-[#F5A623] border-[#F5A623]/20'}`}>
                {selectedBrand === 'trio_bliss' ? 'Trio Bliss' : 'Wittyshadows'}
              </Badge>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Source */}
            <div>
              <Label className="text-zinc-400 text-xs uppercase">Source of Lead *</Label>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {(selectedBrand === 'trio_bliss' ? LEAD_SOURCES_TB : LEAD_SOURCES_WS).map(s => (
                  <button key={s} type="button" onClick={() => setNewLead({...newLead, lead_source: s})}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                      newLead.lead_source === s ? 'bg-[#F5A623]/20 text-[#F5A623] border border-[#F5A623]/30' : 'bg-[#0A0A0A] text-zinc-400 border border-white/10 hover:border-white/20'
                    }`} data-testid={`source-${s.toLowerCase()}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
            {newLead.lead_source === 'Other' && (
              <div>
                <Label className="text-zinc-400 text-xs uppercase">Specify Source</Label>
                <Input value={newLead.lead_source_other} onChange={e => setNewLead({...newLead, lead_source_other: e.target.value})}
                  className="bg-[#0A0A0A] border-white/10 text-white mt-1" placeholder="Where did this lead come from?" data-testid="lead-source-other" />
              </div>
            )}

            {/* Wittyshadows-specific: Event Date */}
            {selectedBrand === 'wittyshadows' && (
              <div>
                <Label className="text-zinc-400 text-xs uppercase">Date of Event</Label>
                <Input type="date" value={newLead.event_date} onChange={e => setNewLead({...newLead, event_date: e.target.value})}
                  className="bg-[#0A0A0A] border-white/10 text-white mt-1" data-testid="lead-event-date" />
              </div>
            )}

            {/* Name & Phone */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-400 text-xs uppercase">Name *</Label>
                <Input value={newLead.client_name} onChange={e => setNewLead({...newLead, client_name: e.target.value})}
                  className="bg-[#0A0A0A] border-white/10 text-white mt-1" placeholder="Client name" data-testid="lead-name-input" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase">Phone Number *</Label>
                <Input value={newLead.phone} onChange={e => setNewLead({...newLead, phone: e.target.value})}
                  className="bg-[#0A0A0A] border-white/10 text-white mt-1" placeholder="9876543210" data-testid="lead-phone-input" />
              </div>
            </div>

            {/* Screenshot Upload (Wittyshadows) */}
            {selectedBrand === 'wittyshadows' && (
              <div>
                <Label className="text-zinc-400 text-xs uppercase">Screenshot</Label>
                <div className="mt-1">
                  {screenshotPreview ? (
                    <div className="relative">
                      <img src={screenshotPreview} alt="Preview" className="w-full h-40 object-cover rounded border border-white/10" />
                      <button onClick={() => { setScreenshotFile(null); setScreenshotPreview(null); }}
                        className="absolute top-2 right-2 p-1 bg-black/70 rounded-full hover:bg-black/90">
                        <X className="h-4 w-4 text-white" />
                      </button>
                      <p className="text-xs text-zinc-500 mt-1">Image will be compressed 50% on upload</p>
                    </div>
                  ) : (
                    <label className="flex items-center justify-center gap-2 p-6 border-2 border-dashed border-white/10 rounded-lg cursor-pointer hover:border-[#F5A623]/30 transition-colors">
                      <Upload className="h-5 w-5 text-zinc-500" />
                      <span className="text-sm text-zinc-400">Click to upload screenshot</span>
                      <input type="file" accept="image/*" className="hidden" onChange={handleScreenshotSelect} data-testid="lead-screenshot-input" />
                    </label>
                  )}
                </div>
              </div>
            )}

            {/* Assigned To (Admin/Manager only) */}
            {isManager && (
              <div>
                <Label className="text-zinc-400 text-xs uppercase">Assign To</Label>
                <div className="max-h-28 overflow-y-auto space-y-1 bg-[#0A0A0A] rounded-md border border-white/10 p-2 mt-1">
                  {salesPeople.length === 0 ? (
                    <p className="text-xs text-zinc-600 text-center py-2">No sales people yet. Add them in Team page.</p>
                  ) : (
                    salesPeople.map(sp => (
                      <button key={sp.id} type="button" onClick={() => setNewLead({...newLead, assigned_to: sp.id})}
                        className={`w-full flex items-center justify-between px-2 py-1.5 text-left text-xs rounded transition-colors ${
                          newLead.assigned_to === sp.id ? 'bg-[#F5A623]/20 text-[#F5A623]' : 'text-zinc-300 hover:bg-white/10'
                        }`} data-testid={`assign-lead-${sp.id}`}>
                        <span>{sp.name}</span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Notes */}
            <div>
              <Label className="text-zinc-400 text-xs uppercase">Initial Notes</Label>
              <Textarea value={newLead.notes} onChange={e => setNewLead({...newLead, notes: e.target.value})}
                rows={2} className="bg-[#0A0A0A] border-white/10 text-white mt-1" placeholder="First conversation details..." />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowCreateDialog(false)} className="border-white/10 text-white hover:bg-white/5">Cancel</Button>
              <Button onClick={handleCreateLead} disabled={uploading}
                className="bg-[#F5A623] hover:bg-[#F5A623]/90 text-black" data-testid="submit-lead-btn">
                {uploading ? 'Creating...' : 'Create Lead'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== LEAD DETAIL SHEET ===== */}
      <Sheet open={showDetailSheet} onOpenChange={setShowDetailSheet}>
        <SheetContent className="bg-[#0A0A0A] border-white/10 text-white w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-white flex items-center gap-2">
              {selectedLead?.client_name}
              {selectedLead?.brand && (
                <Badge variant="outline" className={`text-xs ${selectedLead.brand === 'trio_bliss' ? 'bg-purple-500/10 text-purple-400 border-purple-500/20' : 'bg-blue-500/10 text-blue-400 border-blue-500/20'}`}>
                  {selectedLead.brand === 'trio_bliss' ? 'Trio Bliss' : 'WS'}
                </Badge>
              )}
            </SheetTitle>
          </SheetHeader>
          {selectedLead && (
            <div className="space-y-5 mt-4" data-testid="lead-detail">
              {/* Contact with actions */}
              <div className="p-3 bg-[#121212] rounded-lg border border-white/5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-zinc-500" />
                    <span className="text-white font-medium">{selectedLead.phone}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Button size="sm" variant="outline" className="h-7 px-2 text-xs border-white/10 text-zinc-400 hover:text-white" data-testid="copy-phone"
                      onClick={() => copyToClipboard(selectedLead.phone)}>
                      <Copy className="h-3 w-3 mr-1" />Copy
                    </Button>
                    <Button size="sm" className="h-7 px-2 text-xs bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="wa-detail-btn"
                      onClick={() => openWhatsApp(selectedLead.phone, selectedLead.client_name)}>
                      <MessageCircle className="h-3 w-3 mr-1" />WhatsApp
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 px-2 text-xs border-white/10 text-zinc-400 hover:text-white"
                      onClick={() => window.open(`tel:${selectedLead.phone}`)}>
                      <PhoneCall className="h-3 w-3 mr-1" />Call
                    </Button>
                  </div>
                </div>
                {selectedLead.email && <div className="flex items-center gap-2 text-sm mt-2"><Mail className="h-3.5 w-3.5 text-zinc-500" /><span className="text-zinc-300">{selectedLead.email}</span></div>}
              </div>

              {/* Call Status & Log Call */}
              <div className="p-3 bg-[#121212] rounded-lg border border-white/5">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-zinc-400 text-xs uppercase">Call Status</Label>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={`text-xs ${
                      selectedLead.call_status === 'connected' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
                      selectedLead.call_status === 'not_connected' ? 'bg-rose-500/10 text-rose-400 border-rose-500/20' :
                      'bg-zinc-500/10 text-zinc-400 border-zinc-500/20'
                    }`}>
                      {selectedLead.call_status === 'connected' ? 'Connected' : selectedLead.call_status === 'not_connected' ? 'Not Connected' : 'Pending'}
                    </Badge>
                    {selectedLead.wa_message_sent && <Badge variant="outline" className="text-[10px] bg-emerald-500/10 text-emerald-400 border-emerald-500/20">WA Sent</Badge>}
                  </div>
                </div>
                {selectedLead.lead_quality && (
                  <p className="text-xs text-zinc-400 mb-2">Quality: <span className={`font-semibold ${selectedLead.lead_quality === 'hot' ? 'text-rose-400' : selectedLead.lead_quality === 'warm' ? 'text-amber-400' : 'text-blue-400'}`}>{selectedLead.lead_quality.toUpperCase()}</span></p>
                )}
                <Button size="sm" className="w-full bg-[#F5A623]/10 hover:bg-[#F5A623]/20 text-[#F5A623] border border-[#F5A623]/20" data-testid="log-call-btn"
                  onClick={() => { setCallForm({ connected: false, notes: '', callback_time: '', lead_quality: '', quote_amount: '', follow_up_date: '', wa_message_sent: false }); setShowCallLog(true); }}>
                  <Phone className="h-3.5 w-3.5 mr-1.5" /> Log Call
                </Button>

                {/* Call History */}
                {selectedLead.call_logs?.length > 0 && (
                  <div className="mt-3 space-y-1.5 max-h-32 overflow-y-auto">
                    <p className="text-xs text-zinc-500 font-medium">Call History ({selectedLead.call_logs.length})</p>
                    {selectedLead.call_logs.slice().reverse().map((cl, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs p-1.5 rounded bg-white/[0.02]">
                        {cl.connected ? <PhoneCall className="h-3 w-3 text-emerald-400 shrink-0" /> : <PhoneOff className="h-3 w-3 text-rose-400 shrink-0" />}
                        <span className="text-zinc-400 flex-1 truncate">{cl.notes || (cl.connected ? 'Connected' : 'Not connected')}</span>
                        {cl.wa_message_sent && <MessageCircle className="h-3 w-3 text-emerald-400 shrink-0" />}
                        <span className="text-zinc-600 shrink-0">{formatDateTime(cl.timestamp)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Info Grid */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-[#121212] rounded border border-white/5">
                  <p className="text-xs text-zinc-500">Source</p>
                  <p className="text-white text-sm">{selectedLead.lead_source}{selectedLead.lead_source_other ? ` (${selectedLead.lead_source_other})` : ''}</p>
                </div>
                <div className="p-3 bg-[#121212] rounded border border-white/5">
                  <p className="text-xs text-zinc-500">Quote</p>
                  <p className="text-[#F5A623] text-sm font-medium">{formatCurrency(selectedLead.quote_amount)}</p>
                </div>
                {selectedLead.event_type && (
                  <div className="p-3 bg-[#121212] rounded border border-white/5">
                    <p className="text-xs text-zinc-500">Event</p>
                    <p className="text-white text-sm">{selectedLead.event_type}</p>
                    {selectedLead.event_date && <p className="text-xs text-zinc-500 mt-0.5">{formatDate(selectedLead.event_date)}</p>}
                  </div>
                )}
                {selectedLead.assigned_to_name && (
                  <div className="p-3 bg-[#121212] rounded border border-white/5">
                    <p className="text-xs text-zinc-500">Assigned To</p>
                    <p className="text-white text-sm">{selectedLead.assigned_to_name}</p>
                  </div>
                )}
              </div>

              {/* Screenshots */}
              <div className="p-3 bg-[#121212] rounded-lg border border-white/5">
                <Label className="text-zinc-400 text-xs uppercase mb-2 block">Screenshots ({selectedLead.screenshots?.length || 0})</Label>
                {selectedLead.screenshots?.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    {selectedLead.screenshots.map((s, i) => (
                      <a key={i} href={`${process.env.REACT_APP_BACKEND_URL}${s.url}`} target="_blank" rel="noreferrer"
                        className="block border border-white/10 rounded overflow-hidden hover:border-[#F5A623]/30">
                        <img src={`${process.env.REACT_APP_BACKEND_URL}${s.url}`} alt={`SS ${i+1}`} className="w-full h-20 object-cover" />
                      </a>
                    ))}
                  </div>
                )}
                <label className="flex items-center justify-center gap-2 p-3 border border-dashed border-white/10 rounded cursor-pointer hover:border-[#F5A623]/30">
                  <Upload className="h-4 w-4 text-zinc-500" />
                  <span className="text-xs text-zinc-400">Upload screenshot</span>
                  <input type="file" accept="image/*" className="hidden" data-testid="detail-screenshot-upload"
                    onChange={e => { if (e.target.files[0]) handleScreenshotUploadForLead(selectedLead.id, e.target.files[0]); }} />
                </label>
              </div>

              {/* Stage & Actions */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-zinc-400 text-xs uppercase">Pipeline Stage</Label>
                  <Badge variant="outline" className={getStageBadge(selectedLead.pipeline_stage)}>{(selectedLead.pipeline_stage || 'new_enquiry').replace(/_/g, ' ')}</Badge>
                </div>
                {selectedLead.pipeline_stage !== 'booked' && selectedLead.pipeline_stage !== 'lost' && (
                  <div className="flex gap-2 flex-wrap">
                    {PIPELINE_STAGES.filter(s => s.value !== selectedLead.pipeline_stage && s.value !== 'lost').map(s => (
                      <Button key={s.value} size="sm" variant="outline"
                        className="text-xs h-7 border-white/10 text-zinc-400 hover:bg-white/5"
                        onClick={() => { handleStageChange(selectedLead.id, s.value); setShowDetailSheet(false); }}>
                        <ArrowRight className="h-3 w-3 mr-1" /> {s.label}
                      </Button>
                    ))}
                    <Button size="sm" variant="outline" className="text-xs h-7 border-rose-500/20 text-rose-400 hover:bg-rose-500/10"
                      onClick={() => { setLostForm({ lost_reason: '', lost_notes: '' }); setShowLostDialog(true); }}>
                      <XCircle className="h-3 w-3 mr-1" /> Lost
                    </Button>
                    <Button size="sm" className="text-xs h-7 bg-emerald-500 hover:bg-emerald-600 text-white"
                      onClick={() => { setConvertForm({ advance_amount: '', project_title: '' }); setShowConvertDialog(true); }}>
                      <CheckCircle className="h-3 w-3 mr-1" /> Book
                    </Button>
                  </div>
                )}
                {selectedLead.pipeline_stage === 'booked' && (
                  <div className="p-3 bg-emerald-500/10 rounded border border-emerald-500/20">
                    <p className="text-emerald-400 text-sm font-medium flex items-center gap-2"><CheckCircle className="h-4 w-4" /> Converted to Booking</p>
                  </div>
                )}
                {selectedLead.pipeline_stage === 'lost' && (
                  <div className="p-3 bg-rose-500/10 rounded border border-rose-500/20">
                    <p className="text-rose-400 text-sm font-medium flex items-center gap-2"><XCircle className="h-4 w-4" /> Lost: {selectedLead.lost_reason}</p>
                  </div>
                )}
              </div>

              {/* Follow-up */}
              <div className="p-3 bg-[#121212] rounded border border-white/5">
                <Label className="text-zinc-400 text-xs uppercase">Next Follow-up</Label>
                <Input type="datetime-local" defaultValue={selectedLead.next_follow_up ? new Date(selectedLead.next_follow_up).toISOString().slice(0, 16) : ''}
                  onBlur={(e) => { if (e.target.value) handleUpdateField(selectedLead.id, 'next_follow_up', new Date(e.target.value).toISOString()); }}
                  className="bg-[#0A0A0A] border-white/10 text-white mt-1" data-testid="follow-up-input" />
              </div>

              {/* Notes / Conversation Log */}
              <div>
                <Label className="text-zinc-400 text-xs uppercase mb-2 block">Conversation Log ({selectedLead.notes?.length || 0})</Label>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {(selectedLead.notes || []).slice().reverse().map((note, i) => (
                    <div key={i} className="p-3 bg-[#121212] rounded border border-white/5">
                      <p className="text-white text-sm">{note.text}</p>
                      <p className="text-xs text-zinc-600 mt-1">{note.added_by_name} · {formatDateTime(note.timestamp)}</p>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 mt-3">
                  <Input value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Add a note..."
                    className="bg-[#0A0A0A] border-white/10 text-white" data-testid="note-input"
                    onKeyDown={(e) => { if (e.key === 'Enter') handleAddNote(); }} />
                  <Button onClick={handleAddNote} className="bg-[#F5A623] hover:bg-[#F5A623]/90 text-black shrink-0" data-testid="add-note-btn">
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* ===== CALL LOG DIALOG ===== */}
      <Dialog open={showCallLog} onOpenChange={setShowCallLog}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md">
          <DialogHeader><DialogTitle>Log Call — {selectedLead?.client_name}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Connected / Not Connected */}
            <div className="grid grid-cols-2 gap-3">
              <button onClick={() => setCallForm({...callForm, connected: true})}
                className={`p-4 rounded-lg border-2 text-center transition-all ${callForm.connected ? 'border-emerald-500 bg-emerald-500/10' : 'border-white/10 hover:border-emerald-500/30'}`}
                data-testid="call-connected">
                <PhoneCall className={`h-6 w-6 mx-auto mb-1 ${callForm.connected ? 'text-emerald-400' : 'text-zinc-500'}`} />
                <p className={`text-sm font-medium ${callForm.connected ? 'text-emerald-400' : 'text-zinc-400'}`}>Connected</p>
              </button>
              <button onClick={() => setCallForm({...callForm, connected: false})}
                className={`p-4 rounded-lg border-2 text-center transition-all ${!callForm.connected ? 'border-rose-500 bg-rose-500/10' : 'border-white/10 hover:border-rose-500/30'}`}
                data-testid="call-not-connected">
                <PhoneOff className={`h-6 w-6 mx-auto mb-1 ${!callForm.connected ? 'text-rose-400' : 'text-zinc-500'}`} />
                <p className={`text-sm font-medium ${!callForm.connected ? 'text-rose-400' : 'text-zinc-400'}`}>Not Connected</p>
              </button>
            </div>

            {/* If Connected */}
            {callForm.connected && (
              <>
                <div>
                  <Label className="text-zinc-400 text-xs uppercase">Quality of Lead</Label>
                  <div className="flex gap-2 mt-1">
                    {['hot', 'warm', 'cold'].map(q => (
                      <button key={q} onClick={() => setCallForm({...callForm, lead_quality: q})}
                        className={`px-4 py-2 rounded border text-sm font-medium capitalize transition-colors ${
                          callForm.lead_quality === q
                            ? q === 'hot' ? 'border-rose-500 bg-rose-500/10 text-rose-400' : q === 'warm' ? 'border-amber-500 bg-amber-500/10 text-amber-400' : 'border-blue-500 bg-blue-500/10 text-blue-400'
                            : 'border-white/10 text-zinc-500 hover:border-white/20'
                        }`} data-testid={`quality-${q}`}>{q}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs uppercase">Quotation Amount</Label>
                  <Input type="number" value={callForm.quote_amount} onChange={e => setCallForm({...callForm, quote_amount: e.target.value})}
                    className="bg-[#0A0A0A] border-white/10 text-white mt-1" placeholder="150000" data-testid="call-quote-amount" />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs uppercase">Follow-up Date</Label>
                  <Input type="datetime-local" value={callForm.follow_up_date} onChange={e => setCallForm({...callForm, follow_up_date: e.target.value})}
                    className="bg-[#0A0A0A] border-white/10 text-white mt-1" data-testid="call-followup-date" />
                </div>
              </>
            )}

            {/* If Not Connected */}
            {!callForm.connected && (
              <>
                <div>
                  <Label className="text-zinc-400 text-xs uppercase">Callback Reminder</Label>
                  <div className="flex gap-2 mt-1 mb-2">
                    <Button size="sm" variant="outline" className="text-xs border-white/10 text-zinc-400 hover:text-white"
                      onClick={() => {
                        const tomorrow = new Date();
                        tomorrow.setDate(tomorrow.getDate() + 1);
                        tomorrow.setHours(10, 0, 0, 0);
                        setCallForm({...callForm, callback_time: tomorrow.toISOString().slice(0, 16)});
                      }}>Tomorrow 10 AM</Button>
                    <Button size="sm" variant="outline" className="text-xs border-white/10 text-zinc-400 hover:text-white"
                      onClick={() => {
                        const later = new Date();
                        later.setHours(later.getHours() + 2);
                        setCallForm({...callForm, callback_time: later.toISOString().slice(0, 16)});
                      }}>In 2 Hours</Button>
                  </div>
                  <Input type="datetime-local" value={callForm.callback_time} onChange={e => setCallForm({...callForm, callback_time: e.target.value})}
                    className="bg-[#0A0A0A] border-white/10 text-white" data-testid="call-callback-time" />
                </div>
              </>
            )}

            {/* WA Message */}
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={callForm.wa_message_sent} onCheckedChange={v => setCallForm({...callForm, wa_message_sent: v})} />
              <span className="text-sm text-zinc-300">WhatsApp message dropped</span>
            </label>

            {/* Notes */}
            <div>
              <Label className="text-zinc-400 text-xs uppercase">Call Notes</Label>
              <Textarea value={callForm.notes} onChange={e => setCallForm({...callForm, notes: e.target.value})}
                rows={2} className="bg-[#0A0A0A] border-white/10 text-white mt-1" placeholder="What happened on the call..." data-testid="call-notes" />
            </div>

            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowCallLog(false)} className="border-white/10 text-white hover:bg-white/5">Cancel</Button>
              <Button onClick={handleLogCall} className="bg-[#F5A623] hover:bg-[#F5A623]/90 text-black" data-testid="save-call-log">Save Call Log</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== LOST DIALOG ===== */}
      <Dialog open={showLostDialog} onOpenChange={setShowLostDialog}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md">
          <DialogHeader><DialogTitle>Mark Lead as Lost</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-zinc-400 text-xs uppercase">Reason</Label>
              <Input
                value={lostForm.lost_reason}
                onChange={(e) => setLostForm({...lostForm, lost_reason: e.target.value})}
                placeholder="e.g. Went to competitor, Budget too low…"
                className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                data-testid="lost-reason-input"
              />
              <div className="flex flex-wrap gap-1.5 mt-2">
                {LOST_REASONS.map(r => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setLostForm({...lostForm, lost_reason: r})}
                    className={`text-[11px] px-2 py-1 rounded border transition-colors ${
                      lostForm.lost_reason === r
                        ? 'border-rose-500 bg-rose-500/10 text-rose-300'
                        : 'border-white/10 text-zinc-400 hover:border-white/20 hover:text-white'
                    }`}
                    data-testid={`lost-reason-chip-${r.replace(/\s+/g,'-').toLowerCase()}`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>
            <Textarea value={lostForm.lost_notes} onChange={(e) => setLostForm({...lostForm, lost_notes: e.target.value})} rows={2} className="bg-[#0A0A0A] border-white/10 text-white" placeholder="Additional notes (optional)..." />
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowLostDialog(false)} className="border-white/10 text-white hover:bg-white/5">Cancel</Button>
              <Button onClick={handleMarkLost} className="bg-rose-500 hover:bg-rose-600 text-white" data-testid="confirm-lost-btn">Mark as Lost</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== CONVERT DIALOG ===== */}
      <Dialog open={showConvertDialog} onOpenChange={setShowConvertDialog}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md">
          <DialogHeader><DialogTitle>Convert to Booking</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-3 bg-emerald-500/10 rounded border border-emerald-500/20">
              <p className="text-emerald-400 text-sm font-medium">Converting: {selectedLead?.client_name}</p>
              <p className="text-xs text-emerald-300/70 mt-1">Creates a Client + Project automatically.</p>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase">Advance Payment *</Label>
              <Input type="number" value={convertForm.advance_amount} onChange={(e) => setConvertForm({...convertForm, advance_amount: e.target.value})}
                placeholder="50000" className="bg-[#0A0A0A] border-white/10 text-white mt-1" data-testid="advance-amount-input" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase">Project Title</Label>
              <Input value={convertForm.project_title} onChange={(e) => setConvertForm({...convertForm, project_title: e.target.value})}
                placeholder={`${selectedLead?.client_name} - ${selectedLead?.event_type || 'Project'}`} className="bg-[#0A0A0A] border-white/10 text-white mt-1" />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowConvertDialog(false)} className="border-white/10 text-white hover:bg-white/5">Cancel</Button>
              <Button onClick={handleConvert} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="confirm-convert-btn">
                <CheckCircle className="h-4 w-4 mr-2" /> Confirm Booking
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== LEAD CARD COMPONENT ====================

const QualityButtons = ({ leadId, current, onSet }) => {
  const opts = [
    { v: 'good', label: 'Good', cls: 'border-emerald-500/40 text-emerald-400 bg-emerald-500/10', activeCls: 'border-emerald-500 bg-emerald-500/20 text-emerald-300' },
    { v: 'ok',   label: 'OK',   cls: 'border-amber-500/40 text-amber-400 bg-amber-500/10',     activeCls: 'border-amber-500 bg-amber-500/20 text-amber-300' },
    { v: 'bad',  label: 'Bad',  cls: 'border-rose-500/40 text-rose-400 bg-rose-500/10',         activeCls: 'border-rose-500 bg-rose-500/20 text-rose-300' },
  ];
  // Map legacy hot/warm/cold values so they still show as active
  const norm = (v) => {
    const x = (v || '').toLowerCase();
    if (x === 'hot') return 'good';
    if (x === 'warm') return 'ok';
    if (x === 'cold') return 'bad';
    return x;
  };
  const cur = norm(current);
  return (
    <div className="flex items-center gap-1" data-testid={`lead-quality-${leadId}`}>
      {opts.map(o => {
        const active = cur === o.v;
        return (
          <button
            key={o.v}
            onClick={(e) => { e.stopPropagation(); onSet(leadId, o.v); }}
            className={`text-[10px] px-2 py-0.5 rounded border font-medium transition-colors ${active ? o.activeCls : 'border-white/10 text-zinc-500 hover:' + o.cls}`}
            data-testid={`lead-quality-${leadId}-${o.v}`}
            title={`Tag as ${o.label}`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
};

const LeadCard = ({ lead, onDragStart, onClick, onPhoneAction, showPhoneActions, phoneActionsRef, onSetQuality }) => (
  <div draggable onDragStart={(e) => onDragStart(e, lead)} onClick={() => onClick(lead)}
    className="bg-[#121212] border border-white/10 rounded-lg p-3 cursor-pointer hover:border-[#F5A623]/30 transition-colors group"
    data-testid={`lead-card-${lead.id}`}>
    <div className="flex items-start justify-between mb-2">
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-white font-medium text-sm">{lead.client_name}</p>
        <Badge variant="outline" className={`text-[10px] ${lead.brand === 'trio_bliss' ? 'bg-purple-500/10 text-purple-400 border-purple-500/20' : 'bg-blue-500/10 text-blue-400 border-blue-500/20'}`}>
          {lead.brand === 'trio_bliss' ? 'TB' : 'WS'}
        </Badge>
        {lead.lead_score != null && (
          <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold border ${getScoreColor(lead.score_label)}`}>
            <span className={`inline-block h-1.5 w-1.5 rounded-full mr-0.5 ${getScoreDot(lead.score_label)}`} />{lead.lead_score}
          </span>
        )}
      </div>
      <GripVertical className="h-3.5 w-3.5 text-zinc-600 group-hover:text-zinc-400 shrink-0" />
    </div>

    <div className="flex items-center gap-2 mb-2">
      <div className="relative" ref={showPhoneActions ? phoneActionsRef : null}>
        <button onClick={(e) => { e.stopPropagation(); onPhoneAction(lead.id); }}
          className="text-xs text-zinc-500 hover:text-white flex items-center gap-1 transition-colors">
          <Phone className="h-3 w-3" />{lead.phone}
        </button>
        {showPhoneActions && (
          <div className="absolute left-0 top-full mt-1 bg-[#1a1a1a] border border-white/10 rounded-lg shadow-xl z-50 py-1 min-w-[140px]">
            <button onClick={(e) => { e.stopPropagation(); copyToClipboard(lead.phone); onPhoneAction(null); }}
              className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-zinc-300 hover:bg-white/5"><Copy className="h-3 w-3" />Copy</button>
            <button onClick={(e) => { e.stopPropagation(); openWhatsApp(lead.phone, lead.client_name); onPhoneAction(null); }}
              className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-emerald-400 hover:bg-white/5"><MessageCircle className="h-3 w-3" />WhatsApp</button>
            <button onClick={(e) => { e.stopPropagation(); window.open(`tel:${lead.phone}`); onPhoneAction(null); }}
              className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-blue-400 hover:bg-white/5"><PhoneCall className="h-3 w-3" />Call</button>
          </div>
        )}
      </div>
      {lead.call_status === 'not_connected' && <PhoneOff className="h-3 w-3 text-rose-400" />}
      {lead.call_status === 'connected' && <PhoneCall className="h-3 w-3 text-emerald-400" />}
    </div>

    <div className="flex items-center justify-between text-xs">
      <span className="text-zinc-500">{lead.lead_source}</span>
      {lead.quote_amount && <span className="text-[#F5A623]">{formatCurrency(lead.quote_amount)}</span>}
    </div>

    {lead.follow_up_status && (
      <div className={`mt-2 text-xs flex items-center gap-1 ${lead.follow_up_status === 'overdue' ? 'text-rose-400' : lead.follow_up_status === 'due_today' ? 'text-amber-400' : 'text-zinc-500'}`}>
        <Clock className="h-3 w-3" />
        {lead.follow_up_status === 'due_today' ? 'Follow-up today' : lead.follow_up_status === 'overdue' ? 'Overdue!' : formatDate(lead.next_follow_up)}
      </div>
    )}

    {lead.screenshots?.length > 0 && (
      <div className="mt-1.5 text-xs text-zinc-500 flex items-center gap-1"><Image className="h-3 w-3" />{lead.screenshots.length} screenshot{lead.screenshots.length > 1 ? 's' : ''}</div>
    )}

    {onSetQuality && (
      <div className="mt-2 pt-2 border-t border-white/5" onClick={(e) => e.stopPropagation()}>
        <QualityButtons leadId={lead.id} current={lead.lead_quality} onSet={onSetQuality} />
      </div>
    )}
  </div>
);

export default LeadsPage;
